import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import PageLayout from '../../components/layout/PageLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import { supabaseHelpers } from '../../lib/supabase';

const QuickCalculator = () => {
  const publicMode = import.meta.env?.VITE_PUBLIC_MODE === 'true';
  // Layout state managed by PageLayout

  const [printers, setPrinters] = useState([]);
  const [filaments, setFilaments] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [selectedFilament, setSelectedFilament] = useState('');
  const [duration, setDuration] = useState('');
  const [weight, setWeight] = useState('');
  const [clientMarkup, setClientMarkup] = useState(50);
  const [electricityRate, setElectricityRate] = useState(0.75);
  const [materialWaste, setMaterialWaste] = useState(5);
  const [isCalculating, setIsCalculating] = useState(false);

  const [costs, setCosts] = useState({
    material: 0,
    electricity: 0,
    machine: 0,
    waste: 0,
    total: 0,
    profit: 0,
    finalPrice: 0,
    perUnit: 0
  });

  const { user } = useAuth();

  // Fetch printers and filaments for the current user
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!user?.id) {
          // Modo público: disponibiliza opções padrão para explorar sem login
          if (publicMode) {
            setPrinters([
              {
                id: 'default-printer',
                brand: 'Padrão',
                model: 'Sem cadastro',
                hourly_rate: 20,
                power_consumption: 150
              }
            ]);
            setFilaments([
              {
                id: 'default-filament',
                brand: 'Padrão',
                material: 'PLA',
                color: 'preto',
                price_per_kg: 120
              }
            ]);
          }
          setLoadingData(false);
          return;
        }
        const [{ data: userPrinters }, { data: userFilaments }] = await Promise.all([
          supabaseHelpers?.getUserPrinters(user?.id),
          supabaseHelpers?.getUserFilaments(user?.id)
        ]);

        setPrinters(userPrinters || []);
        setFilaments(userFilaments || []);
      } catch (err) {
        console.error('Erro ao carregar dados:', err);
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, [user]);

  const isFormValid = selectedPrinter && selectedFilament && duration && weight;

  const calculateCosts = useCallback(() => {
    if (!isFormValid) return;
    setIsCalculating(true);

    setTimeout(() => {
      const printer = printers?.find((p) => p?.id === selectedPrinter);
      const filament = filaments?.find((f) => f?.id === selectedFilament);
      const d = parseFloat(duration) || 0;
      const w = parseFloat(weight) || 0;
      const rate = parseFloat(electricityRate) || 0.75;
      const wastePct = parseFloat(materialWaste) || 5;
      const marginPct = parseFloat(clientMarkup) || 50;

      const materialCost = (w / 1000) * (filament?.price_per_kg || 0);
      const powerKwh = (printer?.power_consumption || 220) / 1000;
      const electricityCost = d * powerKwh * rate;
      const machineHourly = parseFloat(printer?.hourly_rate) || 15;
      const machineCost = d * machineHourly;

      const subtotal = materialCost + electricityCost + machineCost;
      const wasteAmount = subtotal * (wastePct / 100);
      const totalBase = subtotal + wasteAmount;

      const profit = totalBase * (marginPct / 100);
      const finalPrice = totalBase + profit;

      setCosts({
        material: materialCost,
        electricity: electricityCost,
        machine: machineCost,
        waste: wasteAmount,
        total: totalBase,
        profit: profit,
        finalPrice: finalPrice,
        perUnit: finalPrice
      });

      setIsCalculating(false);
    }, 300);
  }, [isFormValid, printers, filaments, selectedPrinter, selectedFilament, duration, weight, electricityRate, materialWaste, clientMarkup]);

  useEffect(() => {
    if (isFormValid) {
      calculateCosts();
    }
  }, [isFormValid, calculateCosts]);

  // Removed local layout handlers; PageLayout controls header/sidebar

  const printerOptions = printers?.map((p) => ({
    value: p?.id,
    label: `${p?.brand || ''} ${p?.model || p?.name || ''}`.trim() || 'Impressora'
  }));

  const filamentOptions = filaments?.map((f) => ({
    value: f?.id,
    label: `${f?.brand || ''} ${f?.material || ''} ${f?.color || ''}`.trim() || 'Filamento'
  }));

  return (
    <PageLayout 
      title="Cálculo Rápido" 
      subtitle="Estime o preço com os campos essenciais em segundos" 
      iconName="Zap"
    >
      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Form */}
              <motion.div 
                className="lg:col-span-2 space-y-6"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="glass-panel rounded-2xl overflow-hidden p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Icon name="Settings" size={18} className="text-muted-foreground" />
                    <h3 className="text-lg font-semibold">Parâmetros Essenciais</h3>
                  </div>

                  {loadingData ? (
                    <div className="py-10 text-center">
                      <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                      <p className="text-muted-foreground">Carregando dados...</p>
                    </div>
                  ) : (!user?.id && !publicMode) ? (
                    <div className="py-6">
                      <p className="text-muted-foreground">Faça login para acessar suas impressoras e filamentos.</p>
                      <div className="mt-4">
                        <a href="/authentication-login">
                          <Button size="lg" fullWidth iconName="LogIn" iconPosition="left">Entrar</Button>
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Select
                        label="Impressora"
                        options={printerOptions}
                        value={selectedPrinter}
                        onChange={setSelectedPrinter}
                        searchable
                        clearable
                        placeholder="Selecione a impressora"
                      />

                      <Select
                        label="Filamento"
                        options={filamentOptions}
                        value={selectedFilament}
                        onChange={setSelectedFilament}
                        searchable
                        clearable
                        placeholder="Selecione o filamento"
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Duração (horas)"
                          type="number"
                          step="0.1"
                          placeholder="Ex: 4.5"
                          value={duration}
                          onChange={(e) => setDuration(e?.target?.value)}
                          required
                        />
                        <Input
                          label="Peso (g)"
                          type="number"
                          step="1"
                          placeholder="Ex: 120"
                          value={weight}
                          onChange={(e) => setWeight(e?.target?.value)}
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Input
                          label="Margem de Lucro (%)"
                          type="number"
                          min="0"
                          max="200"
                          step="1"
                          value={clientMarkup}
                          onChange={(e) => setClientMarkup(e?.target?.value)}
                          description="Percentual de lucro aplicado"
                        />
                        <Input
                          label="Desperdício (%)"
                          type="number"
                          min="0"
                          max="50"
                          step="1"
                          value={materialWaste}
                          onChange={(e) => setMaterialWaste(e?.target?.value)}
                          description="Sobra de material e falhas"
                        />
                        <Input
                          label="Tarifa de Energia (R$/kWh)"
                          type="number"
                          step="0.01"
                          value={electricityRate}
                          onChange={(e) => setElectricityRate(e?.target?.value)}
                          description="Tarifa média praticada"
                        />
                      </div>

                      <div className="flex justify-end pt-4 border-t border-border mt-2">
                        <Button 
                          onClick={calculateCosts}
                          iconName="Calculator"
                          iconPosition="left"
                          size="xl"
                          fullWidth
                          className="sm:w-auto w-full"
                          disabled={!isFormValid}
                        >
                          Calcular
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Right: Result */}
              <motion.div 
                className="space-y-6"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="glass-panel rounded-2xl overflow-hidden p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Icon name="Calculator" size={18} className="text-success" />
                    <h3 className="text-lg font-semibold">Resultado</h3>
                  </div>

                  {isCalculating ? (
                    <div className="py-12 text-center">
                      <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                      <p className="text-muted-foreground">Calculando...</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Material</span>
                        <span className="font-semibold">R$ {costs?.material?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Energia</span>
                        <span className="font-semibold">R$ {costs?.electricity?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Máquina</span>
                        <span className="font-semibold">R$ {costs?.machine?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Desperdício</span>
                        <span className="font-semibold">R$ {costs?.waste?.toFixed(2)}</span>
                      </div>

                      <div className="flex justify-between items-center pt-2 border-t border-border">
                        <span className="font-semibold">Subtotal</span>
                        <span className="font-bold">R$ {costs?.total?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-success">Lucro ({clientMarkup}%)</span>
                        <span className="font-bold text-success">R$ {costs?.profit?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xl font-bold">Preço Final</span>
                        <span className="text-xl font-bold text-primary">R$ {costs?.finalPrice?.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
      </div>
    </PageLayout>
  );
};

export default QuickCalculator;